import React, { useState, useEffect } from 'react'
import { Theme, Box, CircularProgress } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { ViewEnum } from '@professocial/professocialTypes/types/commonTypes'
import { PostContentVideo } from '@3m5/sm_publish-frontend-api'
import { useCachedMedia } from '@professocial/professocialFormComponents'

const useStyles = makeStyles((theme: Theme) => ({
  embedResponsive: {
    position: 'relative',
    display: 'block',
    width: '100%',
    padding: '56.25% 0 0 0',
    overflow: 'hidden',
  },
  embedResponsiveItem: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 0,
  },
  embedVideoResponsive: {
    width: '100%',
    height: '100%',
    maxHeight: '500px',
    backgroundColor: 'black'
  },
  videoImage: {
    opacity: '.5',
    width: 'auto',
    maxWidth: '100%',
  },
  videoIcon: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    left: 0,
    top: 0,
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
    paddingBottom: theme.spacing(2),
  },
}))

type IElementProps = {
  value: PostContentVideo
  view?: ViewEnum
}

const VideoPost: React.FC<IElementProps> = (props) => {
  const { url, previewImageUrl } = props.value
  const classes = useStyles()
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  // Use media cache for direct video file playback in preview/template views
  // Do not pass the original URL as fallback to avoid double-fetch; wait for cached Object URL
  const cachedUrl = useCachedMedia(url, undefined)

  // Reset loading state when video URL or its cached version changes
  useEffect(() => {
    if (url) {
      setHasError(false)
      // If we already have a cached URL, don't show the loader to avoid flicker
      setIsLoading(!cachedUrl)
    } else {
      setIsLoading(false)
    }
  }, [url, cachedUrl])

  const handleVideoLoad = () => {
    setIsLoading(false)
    setHasError(false)
  }

  const handleVideoError = () => {
    setIsLoading(false)
    setHasError(true)
  }

  return (
    <>
      {props.view === ViewEnum.preview || props.view === ViewEnum.template || props.view === ViewEnum.templateUser
        ? (
          <div>
            {(!cachedUrl || (isLoading && !hasError)) && (
              <Box className={classes.loadingContainer}>
                <CircularProgress />
              </Box>
            )}
            {cachedUrl && !hasError && (
              <video 
                className={classes.embedVideoResponsive} 
                controls
                onLoadedData={handleVideoLoad}
                onError={handleVideoError}
                style={{ 
                  display: isLoading ? 'none' : 'block'
                }}
              >
                <source src={cachedUrl} type="video/mp4"/>
              </video>
            )}
            {hasError && previewImageUrl && (
              <img src={previewImageUrl} alt='' className={classes.videoImage} />
            )}
          </div>
        )
        : (
          <div className={classes.embedResponsive}>
            <iframe
              className={classes.embedResponsiveItem}
              allow='autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share'
              srcDoc={`<style>*{padding:0;margin:0;overflow:hidden}html,body{height:100%}img,span{position:absolute;width:100%;top:0;bottom:0;margin:auto}span{height:1.5em;text-align:center;font:48px/1.5 sans-serif;color:white;text-shadow:0 0 0.5em black}</style><a href=${url}&show_text=false&autoplay=true><img src=${previewImageUrl} alt=''><span>▶</span></a>`}
              allowFullScreen
            />
          </div>
        )}
    </>
  )
}

export default VideoPost
